/**
 * Play.
 *
 * The whole gesture is "tap a colour, tap another colour". Everything else on
 * screen is feedback: the room takes the colour of your latest mix, the meter
 * tells you how close you are without giving you a number to do arithmetic on,
 * and the pips count what is left of your budget.
 *
 * The bench is reconciled rather than rebuilt on every change, so the blend
 * that just landed keeps its birth animation instead of being replaced by an
 * identical node.
 */

import { h, ICON, svg } from '../dom';
import { ctx, onLeave } from '../ctx';
import { labToLch, isDark } from '../../core/colour';
import { describeDistance } from '../../game/grade';
import type { Slide } from '../../game/session';
import { applyLive, swatch } from '../paint';
import { haptic, flash } from '../motion';
import * as sfx from '../audio';
import { toast, ripple } from '../chrome';
import { puzzleNumber } from '../../core/schedule';

export function playScreen(): HTMLElement {
  const session = ctx.session;
  const puzzle = ctx.puzzle;
  if (!session || !puzzle) throw new Error('playScreen needs an active session');

  const root = h('div', { class: 'screen play' });

  // --- Bar -----------------------------------------------------------------
  root.appendChild(
    h(
      'div',
      { class: 'bar' },
      h(
        'button',
        {
          class: 'icon',
          attrs: { type: 'button', 'aria-label': 'Back to home' },
          on: { click: () => ctx.go('home') },
        },
        svg(ICON.back),
      ),
      h(
        'div',
        { class: 'puzzle-tag' },
        h('div', { class: 'puzzle-tag__n', text: `Puzzle #${puzzleNumber(puzzle.index)}` }),
        h('div', { class: 'puzzle-tag__spec', text: puzzle.spec.label }),
      ),
      h('div', { class: 'bar__spacer' }),
      ctx.stats.streak > 0 &&
        h(
          'div',
          { class: 'streak', data: { hot: ctx.stats.streak >= 3 } },
          h('span', { class: 'streak__flame', text: '🔥' }),
          h('span', { text: `${ctx.stats.streak}` }),
        ),
    ),
  );

  // --- Target --------------------------------------------------------------
  const meterFill = h('div', { class: 'target__fill' });
  const meterRead = h('div', { class: 'target__read', text: 'match this' });

  const target = h(
    'div',
    {
      class: 'target',
      data: { idle: 'true' },
      style: {
        '--target': puzzle.target.hex,
        '--on-target': isDark(puzzle.target.lab) ? '#ffffff' : '#0b0b0e',
      },
      attrs: { role: 'img', 'aria-label': `Target colour, ${puzzle.target.name}` },
    },
    h(
      'div',
      { class: 'target__face' },
      h('div', { class: 'target__label', text: 'Match this' }),
      h('div', { class: 'target__name', text: puzzle.target.name }),
    ),
    h(
      'div',
      { class: 'target__meter' },
      h('div', { class: 'target__track' }, meterFill),
      meterRead,
    ),
  );
  root.appendChild(target);

  // --- Trail ---------------------------------------------------------------
  const trail = h('div', { class: 'trail', attrs: { 'aria-hidden': 'true' } });
  root.appendChild(trail);

  // --- Bench ---------------------------------------------------------------
  const bench = h('div', {
    class: 'bench',
    attrs: { role: 'group', 'aria-label': 'Your colours', 'aria-describedby': 'bench-help' },
  });
  root.appendChild(bench);
  root.appendChild(h('p', { class: 'sr', attrs: { id: 'bench-help' }, text: 'Tap two colours to blend them into a new one.' }));

  // --- Tray ----------------------------------------------------------------
  const pips = h('div', { class: 'pips', attrs: { 'aria-hidden': 'true' } });
  const undoBtn = h('button', {
    class: 'icon',
    attrs: { type: 'button', 'aria-label': 'Undo last blend' },
    on: {
      click: () => {
        sfx.sUndo();
        haptic('undo');
        session.undo();
      },
    },
  }, svg(ICON.undo));

  const submitBtn = h('button', {
    class: 'btn btn--live',
    attrs: { type: 'button' },
    text: 'Submit',
    on: { click: () => session.submit() },
  });

  root.appendChild(
    h('div', { class: 'tray' }, pips, h('div', { class: 'tray__spacer' }), undoBtn, submitBtn),
  );

  // --- Hint ----------------------------------------------------------------
  const hint = h('div', { class: 'hint' });
  if (ctx.stats.played === 0) {
    hint.appendChild(
      h('div', { class: 'hint__bubble', text: 'Tap two colours to mix them' }),
    );
    setTimeout(() => {
      hint.classList.add('hint--out');
      setTimeout(() => hint.remove(), 400);
    }, 5200);
  }
  root.appendChild(hint);

  // ==========================================================================
  // Reconciliation
  // ==========================================================================

  const nodes = new Map<string, HTMLElement>();

  const slideNode = (slide: Slide): HTMLElement => {
    const node = h('button', {
      class: 'slide',
      attrs: {
        type: 'button',
        'aria-label': slide.name ?? 'your mix',
        'aria-pressed': 'false',
      },
      style: swatch(slide),
      on: {
        click: () => {
          sfx.unlock();
          const wasSelected = session.isSelected(slide.id);
          const before = session.blendsUsed;
          session.tap(slide.id);
          if (session.blendsUsed > before) return; // blend() handles its own feedback
          if (wasSelected) sfx.sDeselect();
          else if (session.isSelected(slide.id)) sfx.sSelect();
          haptic('tap');
        },
      },
    });

    node.appendChild(h('span', { class: 'slide__name', text: slide.name ?? '' }));
    nodes.set(slide.id, node);
    return node;
  };

  const paint = (): void => {
    const benchSlides = session.bench;
    const present = new Set(benchSlides.map((s) => s.id));

    // Undo drops slides off the end, so removing anything no longer present is
    // enough to keep order correct.
    for (const [id, node] of nodes) {
      if (!present.has(id)) {
        node.remove();
        nodes.delete(id);
      }
    }

    for (const slide of benchSlides) {
      let node = nodes.get(slide.id);
      if (!node) {
        node = slideNode(slide);
        bench.appendChild(node);
      }
      const selected = session.isSelected(slide.id);
      node.dataset.selected = String(selected);
      node.setAttribute('aria-pressed', String(selected));
      node.dataset.mine = String(slide.depth > 0);
      if (session.answer?.id === slide.id) node.dataset.answer = 'true';
      else delete node.dataset.answer;
    }

    // Budget pips. Rebuilt only when the budget itself changes.
    if (pips.childElementCount !== session.budget) {
      pips.replaceChildren(
        ...Array.from({ length: session.budget }, () => h('span', { class: 'pip' })),
      );
    }

    const spentCount = session.budget - session.remaining;
    [...pips.children].forEach((child, i) => {
      const el = child as HTMLElement;
      const shouldSpend = i < spentCount;
      if (shouldSpend && el.dataset.spent !== 'true') flash(el, 'pip--just', 480);
      el.dataset.spent = String(shouldSpend);
    });

    // Meter. Deliberately wordless: a number invites arithmetic.
    const answer = session.answer;
    if (answer) {
      target.dataset.idle = 'false';
      const precision = Math.max(0, Math.min(1, 1 - session.distance / (puzzle.spec.tolerance * 1.6)));
      meterFill.style.width = `${(precision * 100).toFixed(1)}%`;
      meterRead.textContent = describeDistance(session.distance);
    } else {
      target.dataset.idle = 'true';
      meterFill.style.width = '0%';
      meterRead.textContent = 'match this';
    }

    // Trail.
    const mine = session.bench.filter((s) => s.depth > 0);
    if (trail.childElementCount !== mine.length + (mine.length ? 1 : 0)) {
      trail.replaceChildren(
        ...mine.map((s) => h('span', { class: 'trail__dot', style: swatch(s) })),
        ...(mine.length ? [h('span', { class: 'trail__cap', text: `${mine.length} mixed` })] : []),
      );
    }
    ctx.trail = mine.map((s) => s.hex);

    undoBtn.toggleAttribute('disabled', !session.canUndo);
    submitBtn.toggleAttribute('disabled', !session.canSubmit);
    submitBtn.textContent =
      session.canSubmit && session.remaining > 0 ? `Submit · ${session.remaining} left` : 'Submit';
  };

  // ==========================================================================
  // Events
  // ==========================================================================

  const off = session.on((event) => {
    const answer = session.answer;

    if (event === 'blend' && answer) {
      applyLive(answer.hex, answer.lab);
      const { h: hue } = labToLch(answer.lab);
      sfx.sBlend(hue, answer.lab.L);
      haptic('blend');

      const node = nodes.get(answer.id);
      if (node) {
        flash(node, 'slide--new', 460);
        for (const parentId of answer.parents ?? []) {
          const parent = nodes.get(parentId);
          if (parent) flash(parent, 'slide--pulse', 560);
        }
        // A ring from where the new slide landed, in its own colour.
        const box = node.getBoundingClientRect();
        const benchBox = bench.getBoundingClientRect();
        ripple(bench, box.left - benchBox.left + box.width / 2, box.top - benchBox.top + box.height / 2, answer.hex);
        node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }

    if (event === 'reset') applyLive(answer?.hex ?? puzzle.target.hex);

    paint();

    if (event === 'submit') {
      sfx.sSubmit();
      ctx.finish();
    }
  });
  onLeave(off);

  // Opening state: the room takes the newest thing you own, or a neutral grey.
  applyLive(session.answer?.hex ?? '#8b8b96');
  if (ctx.mode === 'archive') toast('Archive replay — this one will not change your streak');
  paint();

  // Screen-reader announcements for the two things that change silently.
  const live = h('p', { class: 'sr', attrs: { role: 'status', 'aria-live': 'polite' } });
  root.appendChild(live);
  const offAnnounce = session.on((e) => {
    if (e === 'blend') live.textContent = `Mixed ${session.answer?.name ?? 'a colour'}. ${session.remaining} blends left.`;
    else if (e === 'undo') live.textContent = `Undone. ${session.remaining} blends left.`;
  });
  onLeave(offAnnounce);

  return root;
}
